import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Play, TrendingDown, TrendingUp } from "lucide-react";
import { newsFeature } from "../../data/mock";
import SectionHeading from "../ui-custom/SectionHeading";
import GlassCard from "../ui-custom/GlassCard";
import Reveal from "../ui-custom/Reveal";
import { formatNumber } from "../../lib/formatNumber";
import { formatDateTime } from "../../lib/formatDate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const NewsFeatureSection = ({ className = "py-24 md:py-28" }) => {
  // Live share price - same sources as the Investors page chart (Yahoo
  // Finance quote, falling back to Screener's latest daily figures).
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  // Preview image is the video page's own og:image, stored by the media
  // cache refresh; without one the card keeps its plain gradient.
  const [video, setVideo] = useState({ url: newsFeature.videoUrl, image: null });
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/media/featured-video`)
      .then((res) => setVideo((v) => ({ ...v, ...res.data, url: res.data.url || v.url })))
      .catch(() => {});
    axios
      .get(`${API}/investors/stock-quote`)
      .then((res) => setQuote(res.data))
      .catch((err) => setQuoteError(err.response?.data?.detail || `${err.name}: ${err.message}`));
  }, []);
  const isDown = quote ? quote.change_pct < 0 : false;
  const showImage = video.image && !imageFailed;
  return (
    <section className={`relative px-6 ${className}`}>
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          eyebrow="Press Coverage"
          title="In The News"
          subtitle="Quality Power's leadership in the media — latest coverage from the newsroom."
        />

        <Reveal>
          <GlassCard className="p-6 md:p-8 lg:p-10">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Left panel: headline card */}
              <div className="rounded-xl border border-border/10 bg-white/[0.02] p-6 flex flex-col">
                <span className="section-eyebrow mb-3">{newsFeature.tag}</span>
                <h3 className="font-display font-bold uppercase tracking-wide text-lg md:text-xl text-foreground mb-1">
                  {newsFeature.company}
                </h3>
                <p className="text-qamber font-semibold text-sm mb-4">{newsFeature.headline}</p>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed flex-1">{newsFeature.update}</p>

                <div className="mt-6 flex items-center justify-between rounded-lg border border-border/10 px-4 py-3">
                  <div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">{quote?.symbol || "NSE: QPOWER"}</div>
                    <div className="font-display font-bold text-xl text-foreground">
                      {quote ? `₹${formatNumber(quote.price)}` : "—"}
                    </div>
                  </div>
                  {quote && (
                    <div className={`flex items-center gap-1 text-sm font-semibold ${isDown ? "text-red-400" : "text-emerald-400"}`}>
                      {isDown ? <TrendingDown size={16} /> : <TrendingUp size={16} />}
                      {`${quote.change_pct > 0 ? "+" : ""}${formatNumber(quote.change_pct)}%`}
                    </div>
                  )}
                  {quote?.volume != null && (
                    <div className="text-xs text-muted-foreground hidden sm:block">Vol {formatNumber(quote.volume)}</div>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  {quoteError ? (
                    <span className="text-destructive">Share price unavailable: {quoteError}</span>
                  ) : quote ? (
                    <>
                      {quote.as_of
                        ? `As of ${
                            quote.as_of.length === 10 ? `close, ${formatDateTime(quote.as_of).split(",")[0]}` : formatDateTime(quote.as_of)
                          } · `
                        : ""}
                      <Link to="/investors" className="text-qcyan hover:underline">
                        Live chart on Investors
                      </Link>
                    </>
                  ) : (
                    "Loading share price…"
                  )}
                </p>
              </div>

              {/* Right panel: video-style card */}
              <a
                href={video.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Watch on CNBC-TV18: ${newsFeature.segmentTitle}`}
                className="block rounded-xl border border-border/10 overflow-hidden relative group cursor-pointer glow-border-cyan"
              >
                <div className="aspect-video bg-gradient-to-br from-muted to-accent flex items-center justify-center relative">
                  {showImage ? (
                    <>
                      <img
                        src={video.image}
                        alt={video.title || newsFeature.segmentTitle}
                        referrerPolicy="no-referrer"
                        onError={() => setImageFailed(true)}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors" />
                    </>
                  ) : (
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255, 0, 15,0.12),transparent_70%)]" />
                  )}
                  <span className="relative w-16 h-16 rounded-full bg-qcyan/15 border border-qcyan/40 flex items-center justify-center group-hover:scale-110 group-hover:shadow-[0_0_30px_rgba(255, 0, 15,0.5)] transition-all">
                    <Play size={22} className="fill-qcyan text-qcyan ml-1" />
                  </span>
                  <span
                    className={`absolute top-4 left-4 text-[11px] font-semibold tracking-widest uppercase ${
                      showImage ? "text-white bg-black/50 px-2 py-1 rounded" : "text-muted-foreground"
                    }`}
                  >
                    CNBC-TV18 Segment
                  </span>
                </div>
                <div className="p-5 bg-white/[0.02]">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{newsFeature.segmentTitle}</p>
                  <h4 className="font-display font-semibold text-foreground">{newsFeature.spokesperson}</h4>
                  <p className="text-sm text-muted-foreground">{newsFeature.spokespersonTitle}</p>
                  <p className="text-xs text-qcyan font-semibold mt-2">Watch on CNBC-TV18 ↗</p>
                </div>
              </a>
            </div>
          </GlassCard>
        </Reveal>
      </div>
    </section>
  );
};

export default NewsFeatureSection;
